import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { loginSchema, type LoginInput } from '@lyra/shared';
import { signIn } from '@/lib/authClient';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { fieldAria } from '@/lib/formA11y';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  async function onSubmit(values: LoginInput) {
    setFormError(null);
    const { error } = await signIn.email({ email: values.email, password: values.password });
    if (error) { setFormError('Email sau parolă incorecte.'); return; }
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from && from !== '/login' ? from : '/');
  }

  return (
    <div className="mx-auto flex min-h-full max-w-[420px] flex-col justify-center gap-8 px-6 py-16">
      <div>
        <img src="/logo-lyra.png" alt="Lyra" className="h-11 w-auto dark:hidden" />
        <div className="hidden items-center gap-2.5 dark:flex">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-white ring-1 ring-black/5">
            <img src="/logo-lyra-square.png" alt="" className="size-8" />
          </span>
          <span className="text-[26px] font-bold tracking-tight text-ink">Lyra</span>
        </div>
        <p className="mt-2 text-[13.5px] text-ink-3">Sănătate la nivel înalt</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" {...register('email')} {...fieldAria(!!errors.email, 'email-error')} />
          {errors.email && <p id="email-error" className="text-[12.5px] text-bad">{errors.email.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Parolă</Label>
          <Input id="password" type="password" autoComplete="current-password" {...register('password')} {...fieldAria(!!errors.password, 'password-error')} />
          {errors.password && <p id="password-error" className="text-[12.5px] text-bad">{errors.password.message}</p>}
        </div>
        {formError && <p className="text-[13.5px] text-bad">{formError}</p>}
        <Button type="submit" disabled={isSubmitting} className="mt-2">
          {isSubmitting ? 'Se conectează…' : 'Autentificare'}
        </Button>
        <p className="text-[12.5px] text-ink-3">Ați uitat parola? Contactați operatorul pentru resetare.</p>
      </form>
    </div>
  );
}
